import { Component, OnInit, OnDestroy, Inject, ViewChild, ElementRef, HostListener, AfterViewInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { UnsavedReportModalComponent } from '../unsave-report-modal/unsave-report-modal.component';
import { SaveReportModalComponent } from '../save-report-modal/save-report-modal.component';
import { ReportsService } from '@app-reports/services/reports/reports.service';
import {
  CustomReport,
  CustomReportsResponse,
  Metadata,
  ReportDomain,
  ReportTurn,
  ResolutionCandidate,
} from '@app-reports/models/reports.model';
import { API } from '@app-core/constants/api.constants';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { DurationRange } from '@app-core/models/core.model';
import { Store } from '@ngrx/store';
import { getDurationFilterState } from '@app-home/reducers';
import {
  DATE_RANGE_PRESET_TO_DAYS,
  TALK_TO_DATA_DATE_RANGES,
  TALK_TO_DATA_PREDEFINED_CATEGORIES,
} from '@app-reports/constants/reports.constants';
import { TranslateService } from '@ngx-translate/core';
import { AccessService } from '@app-core/services/access/access.service';
import { TaggingService } from '@app-asset-config/services/tagging.service';
import { DataService } from '@app-core/services/data/data.service';
import { DurationRangeService } from '@app-core/services/duration-range/duration-range.service';
import { marked } from 'marked';

interface ChatMessage {
  type: 'user' | 'bot' | 'sql' | 'insights' | 'resolution';
  content: string;
  timestamp: Date;
  isPeriodSelection?: boolean;
  sqlData?: string;
  showSqlExpanded?: boolean;
  renderedContent?: SafeHtml;
  requestId?: string;
  feedback?: 'positive' | 'negative' | null;
  feedbackPopupOpen?: boolean;
  fileCard?: { name: string; rows: number; cols: number; dateRange: string; tagsLabel: string };
  showRetryCard?: boolean;
  candidates?: ResolutionCandidate[];
  originalQuestion?: string;
}

interface LoadingStep {
  label: string;
  state: 'queued' | 'active' | 'done';
}

interface Tag {
  tagId: number;
  tagName: string;
  attributeId?: number;
  attributeName?: string;
  attributeType?: string;
  id?: number;
  name?: string;
}

interface MentionItem {
  label: string;
  icon: string;
  fontSet?: string;
  kind?: 'driver' | 'asset' | 'safety_event' | 'health_event' | 'date';
  id?: string;
  assetid?: string;
  days?: number;
}

interface SelectedMentions {
  drivers: { id: string; name: string }[];
  assets: { assetid: string; name: string }[];
  eventTypes: string[];
  dateRange: { days?: number; startDate?: string; endDate?: string } | null;
}

interface ReportDataRow {
  [key: string]: string | number | boolean | null | undefined;
}

type ConversationState = 'initial' | 'awaiting_period' | 'report_generated';

@Component({
  selector: 'app-talk-to-data-modal',
  templateUrl: './talk-to-data-modal.component.html',
  styleUrls: ['./talk-to-data-modal.component.scss'],
})
export class TalkToDataModalComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('chatContainer') private chatContainer!: ElementRef;
  @ViewChild('homeInput') homeInput!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('clarifyTagFilter') private clarifyTagFilter: any;
  @ViewChild('retryTagFilter') private retryTagFilter: any;
  @ViewChild(SaveReportModalComponent) private saveReportModalRef?: SaveReportModalComponent;

  public tripDetailsForm!: FormGroup;
  public durationControl!: FormControl;
  public chatMessages: ChatMessage[] = [];
  public conversationState: ConversationState = 'initial';
  public isLoading = false;
  public isFirstQuery = true;
  public predefinedCategories = TALK_TO_DATA_PREDEFINED_CATEGORIES;
  public selectedCategoryKey = '';
  public selectedCategoryQuestions: string[] = [];
  public categoryQuestionsLoading = false;

  public onCategorySelect(key: string): void {
    if (this.selectedCategoryKey === key) {
      this.selectedCategoryKey = '';
      this.selectedCategoryQuestions = [];
      return;
    }
    this.selectedCategoryKey = key;

    this.categoryQuestionsLoading = true;
    this.selectedCategoryQuestions = [];

    this.reportService
      .getTalkToDataSuggestedQuestions(key)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.selectedCategoryQuestions = (res.questions ?? []).filter((q) => q.category === key).map((q) => q.question);
          this.categoryQuestionsLoading = false;
        },
        error: () => {
          this.selectedCategoryQuestions = [];
          this.categoryQuestionsLoading = false;
        },
      });
  }

  public submitFeedback(message: ChatMessage, rating: 'positive' | 'negative', comment?: string): void {
    if (!message.requestId || message.feedback) return;
    message.feedback = rating;
    this.closeFeedbackPopup();
    this.reportService
      .postTalkToDataFeedback(message.requestId, rating, this.clientId, this.fleetId, this.feedbackSelectedReasons, comment)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.snackBarService.success(this.translate.instant('Thank you for your feedback! We have recorded your response.'));
        },
        error: () => {
          message.feedback = null;
        },
      });
  }

  public feedbackReasons = ['Factually inaccurate', 'Answer not helpful', 'Did not understand the question', 'Lacks detail'];
  public activeFeedbackMessage: ChatMessage | null = null;
  public feedbackSelectedReasons: string[] = [];
  public feedbackAdditionalText = '';

  public openFeedbackPopup(message: ChatMessage): void {
    if (message.feedback) return;
    this.activeFeedbackMessage = this.activeFeedbackMessage === message ? null : message;
    this.feedbackSelectedReasons = [];
    this.feedbackAdditionalText = '';
    if (this.activeFeedbackMessage) {
      setTimeout(() => this.scrollToBottom(), 100);
    }
  }

  public closeFeedbackPopup(): void {
    this.activeFeedbackMessage = null;
    this.feedbackSelectedReasons = [];
    this.feedbackAdditionalText = '';
  }

  public toggleFeedbackReason(reason: string): void {
    const idx = this.feedbackSelectedReasons.indexOf(reason);
    if (idx === -1) {
      this.feedbackSelectedReasons.push(reason);
    } else {
      this.feedbackSelectedReasons.splice(idx, 1);
    }
  }

  public submitNegativeFeedback(): void {
    if (!this.activeFeedbackMessage) return;
    const parts: string[] = [];
    if (this.feedbackSelectedReasons.length) parts.push(this.feedbackSelectedReasons.join(', '));
    if (this.feedbackAdditionalText?.trim()) parts.push(this.feedbackAdditionalText.trim());
    const comment = parts.length ? parts.join('. ') : undefined;
    this.submitFeedback(this.activeFeedbackMessage, 'negative', comment);
    this.closeFeedbackPopup();
  }

  public modalTitle = 'Custom Report';
  public dataSource: ReportDataRow[] = [];
  public displayedColumns: string[] = [];
  public showNoDataMessage = true;
  public showNoDataRetrySection = false;
  public currentQuestion = '';
  public currentAnswer = '';
  public sessionId = '';
  public sql: string[] = [];
  public executedSql: string[] = [];
  public sql_statements: string[] = [];
  public turns: ReportTurn[] = [];
  public metadata: Metadata = { date_range: '', sql_where_date_columns: [], tag_ids: [], domain: 'trip' };
  public reportPeriods = TALK_TO_DATA_DATE_RANGES;
  public selectedPeriod = 'Past 7 days';
  public durationRange!: DurationRange;

  // v2 canvas state
  public canvasOpen = false;
  public canvasTitle = 'Untitled';
  public selectedCanvasTab = 0;
  public currentChartType = 'bar';
  public homeInputValue = '';

  // clarify card expansion
  public showPeriodExpanded = false;
  public showTagExpanded = false;

  public onTagRowClick(): void {
    const ref = this.clarifyTagFilter ?? this.retryTagFilter;
    ref?.openPanel();
  }

  get selectedPeriodLabel(): string {
    return (
      this.reportPeriods?.find((p: any) => p.days === this.selectedPeriod || p.label === this.selectedPeriod)?.label ??
      this.selectedPeriod ??
      'Last 7 days'
    );
  }

  get selectedTagsLabel(): string {
    if (!this.selectedTagNames?.length) return 'No tags selected';
    return this.selectedTagNames.length === 1
      ? this.selectedTagNames[0]
      : `${this.selectedTagNames[0]} (+${this.selectedTagNames.length - 1} more)`;
  }

  // @ filter panel
  public showAtFilterPanel = false;
  public atFilterCategories = [
    { label: 'Assets', icon: 'local_shipping', value: 'assets', fontSet: 'material-symbols-outlined' },
    { label: 'Drivers', icon: 'person', value: 'drivers', fontSet: 'material-symbols-outlined' },
    { label: 'Date', icon: 'event', value: 'date', fontSet: 'material-icons' },
    { label: 'Events', icon: 'car_crash', value: 'safety_events', fontSet: 'material-symbols-outlined' },
  ];
  public atFilterLevel: 'categories' | 'items' | 'search' = 'categories';
  public atFilterSelectedCategory: { label: string; icon: string; value: string } | null = null;
  public atFilterItems: MentionItem[] = [];
  public atFilterItemsLoading = false;
  public atFilterActiveInput: 'home' | 'chat' = 'chat';
  public atFilterSearch = '';
  public atFilterQuery = '';
  private predefinedFilterReplace: string | null = null;
  public globalAtSearchResults: (MentionItem & {
    categoryValue: string;
    categoryLabel: string;
    categoryIcon: string;
    categoryFontSet?: string;
  })[] = [];
  private globalAtCache: Partial<Record<string, MentionItem[]>> = {};

  public selectedMentions: SelectedMentions = { drivers: [], assets: [], eventTypes: [], dateRange: null };

  get filteredAtItems(): MentionItem[] {
    if (!this.atFilterSearch.trim()) return this.atFilterItems;
    const q = this.atFilterSearch.toLowerCase();
    return this.atFilterItems.filter((i) => i.label.toLowerCase().includes(q));
  }

  // Autocomplete search suggestions
  public searchSuggestions: { text: string; icon: 'history' | 'search' }[] = [];
  public activeSuggestionIndex = -1;
  private suggestionPool: string[] = [];
  public loadingSteps: LoadingStep[] = [];
  public hasResult = false;
  public unSelectAllTag = false;
  public entityType = ['asset', 'driver'];
  public userTags: Tag[] = [];
  public assignedTagList: number[] = [];
  public selectedTagIds: number[] = [];
  public filterSelectedTagIds: number[] = [];
  public selectedTagNames: string[] = [];
  public showSaveReportModal = false;
  public csvLoader = false;
  public saveReportLoader = false;
  public reportName = '';
  public reportDescription = '';
  public isGeneratingInsights = false;
  public showInsightsModal = false;
  public insightsAlreadyGenerated = false;
  public visualsAlreadyGenerated = false;
  public isEditMode = false;
  public editReport: CustomReport | null = null;
  public customReportId = '';
  public reportQuery: string = '';
  public selectedTabIndex = 0;
  public userProfileIcon = '';
  public getCustomReportsListData!: () => void;
  public clientId = '';
  private fleetId = '';
  private allTags: Tag[] = [];
  private attributeList: any[] = [];
  private lastUsedPeriod = '';
  private lastUsedTagIds: number[] = [];
  private lastUsedTagNames: string[] = [];
  public isExplicitPeriodSelection = false;
  private pendingTagSelection: number[] | null = null;
  private isFirstEditQuestion = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private reportService: ReportsService,
    private snackBarService: SnackBarService,
    private translate: TranslateService,
    private accessService: AccessService,
    private taggingService: TaggingService,
    private dialogRef: MatDialogRef<TalkToDataModalComponent>,
    public dataService: DataService,
    public durationService: DurationRangeService,
    private sanitizer: DomSanitizer,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private store: Store<any>,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    const { loginName = '', customerName = '' } = this.accessService.getLoginInfo();

    this.clientId = this.data?.clientId || customerName;
    this.fleetId = this.data?.fleetId;
    this.userProfileIcon = loginName.slice(0, 2);
    this.getCustomReportsListData = this.data?.getCustomReportsListData;
    this.isEditMode = this.data?.isEditMode ?? false;
    this.editReport = this.data?.editReport ?? null;
    this.customReportId = this.data?.customReportId ?? '';
    this.sessionId = this.data?.sessionId ?? '';
    this.reportQuery = this.data?.reportQuery ?? '';
    this.sql = this.data?.sql ?? [];
    this.reportName = this.editReport?.reportName ?? '';
    this.reportDescription = this.editReport?.reportDescription ?? '';
    this.modalTitle = this.isEditMode ? 'What would you like to edit?' : 'Hi! How can I help you?';

    this.durationControl = new FormControl(null);
    this.tripDetailsForm = this.fb.group({
      customQuestion: [''],
    });

    this.calculateDateRange('Past 7 days');

    this.store
      .select(getDurationFilterState)
      .pipe(takeUntil(this.destroy$))
      .subscribe((durationFilter: any) => {
        const { days, displayStartDate: startDate, displayEndDate: endDate } = durationFilter;
        this.durationRange = { days, startDate, endDate };
      });

    this.loadAttributeList();
    this.loadSuggestionPool();

    if (this.isEditMode && this.editReport) {
      this.initializeEditMode();
    } else {
      this.addBotMessage(this.translate.instant('You can select a predefined question or type your own.'));
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.scrollToBottom(), 100);

    this.dialogRef
      .backdropClick()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.hasResult || !this.dataSource?.length) {
          this.dialogRef.close();
          return;
        }
        this.dialog.open(UnsavedReportModalComponent, {
          height: '240px',
          maxWidth: '400px',
          width: '100%',
          disableClose: true,
          autoFocus: false,
          panelClass: ['custom-report-modal'],
          data: {
            onCancelModal: () => this.dialogRef.close(),
            onSaveReport: () => {
              if (this.showSaveReportModal && this.saveReportModalRef) {
                this.saveReportModalRef.onSave();
                return;
              }
              this.openSaveReportModal();
            },
          },
        });
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (this.showPeriodExpanded && !target.closest('.clarify-card') && !target.closest('.clarify-inline-expand')) {
      this.showPeriodExpanded = false;
    }
  }

  public get isInitialState(): boolean {
    return this.isFirstQuery && this.conversationState === 'initial' && !this.isEditMode;
  }

  get atFilterHasNoResults(): boolean {
    if (this.atFilterLevel === 'search') return this.globalAtSearchResults.length === 0;
    if (this.atFilterLevel === 'items') return !this.atFilterItemsLoading && this.filteredAtItems.length === 0;
    return false;
  }

  get homeInputHighlight(): string {
    if (!this.homeInputValue) return '';
    const safe = this.homeInputValue.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return safe.replace(/@\S*/g, (m: string) => `<b>${m}</b>`);
  }

  get chatInputHighlight(): string {
    const value = this.tripDetailsForm.get('customQuestion')?.value ?? '';
    if (!value) return '';
    const safe = value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return safe.replace(/@\S*/g, (m: string) => `<b>${m}</b>`);
  }

  public isAskButtonDisabled(): boolean {
    const question = this.tripDetailsForm.get('customQuestion')?.value?.trim();
    if (this.isLoading) return true;
    if (!question) return true;
    if (this.showAtFilterPanel && this.atFilterActiveInput === 'chat' && this.atFilterHasNoResults) return true;
    return false;
  }

  public onTabChange(event: any): void {
    this.selectedTabIndex = event.index;
  }

  public toggleSqlThinkingForMessage(index: number): void {
    const msg = this.chatMessages[index];
    if (msg?.type === 'sql') {
      msg.showSqlExpanded = !msg.showSqlExpanded;
    }
  }

  private addAtPrefixes(text: string): string {
    return text.replace(/@(\w+)/g, (match: string) => match);
  }

  public formatQuestionHtml(question: string): SafeHtml {
    const translated = this.translate.instant(question);
    const formatted = this.addAtPrefixes(translated).replace(/@(\w+)/g, '<span class="sugg-q-at-mention">@$1</span>');
    return this.sanitizer.bypassSecurityTrustHtml(formatted);
  }

  private readonly PREDEFINED_TOKEN_CATEGORY_MAP: Record<string, string> = {
    asset: 'assets',
    device: 'assets',
    driver: 'drivers',
    event: 'safety_events',
  };

  public onPredefinedQuestionClick(question: string): void {
    if (!this.isFirstQuery || this.conversationState !== 'initial') return;
    const translated = this.translate.instant(question);

    // Detect @asset / @device / @event / @driver / @tag placeholder tokens
    const tokenMatch = translated.match(/@(asset|device|event|driver|tag)\b/i);
    // Detect bare numeric device IDs (10+ digits)
    const deviceMatch = translated.match(/\b(\d{10,})\b/);

    if (tokenMatch) {
      const token = tokenMatch[1].toLowerCase();
      const categoryValue = this.PREDEFINED_TOKEN_CATEGORY_MAP[token];
      const category = this.atFilterCategories.find((c) => c.value === categoryValue);
      if (category) {
        this.predefinedFilterReplace = `@${token}`;
        this.homeInputValue = translated;
        this.atFilterActiveInput = 'home';
        this.atFilterQuery = '';
        this.atFilterSearch = '';
        this.showAtFilterPanel = true;
        this.onAtCategorySelect(category);
        return;
      }
    } else if (/@(?!\w)/.test(translated)) {
      const assetsCategory = this.atFilterCategories.find((c) => c.value === 'assets');
      if (assetsCategory) {
        this.predefinedFilterReplace = '@';
        this.homeInputValue = translated;
        this.atFilterActiveInput = 'home';
        this.atFilterQuery = '';
        this.atFilterSearch = '';
        this.showAtFilterPanel = true;
        this.onAtCategorySelect(assetsCategory);
        return;
      }
    } else if (deviceMatch) {
      const deviceId = deviceMatch[1];
      const assetsCategory = this.atFilterCategories.find((c) => c.value === 'assets');
      if (assetsCategory) {
        this.predefinedFilterReplace = deviceId;
        this.homeInputValue = translated;
        this.atFilterActiveInput = 'home';
        this.atFilterQuery = '';
        this.atFilterSearch = '';
        this.showAtFilterPanel = true;
        this.onAtCategorySelect(assetsCategory);
        return;
      }
    }

    // No placeholder — submit immediately
    const transformedQuestion = this.addAtPrefixes(translated);
    this.currentQuestion = transformedQuestion;
    this.addUserMessage(transformedQuestion);
    this.conversationState = 'report_generated';
    this.executeQuery(transformedQuestion);
  }

  public onConfirmPeriodAndTags(): void {
    if (!this.selectedPeriod) {
      this.snackBarService.failure(this.translate.instant('Please select a period'));
      return;
    }
    if (this.conversationState !== 'awaiting_period') return;

    const tagsToUse = this.filterSelectedTagIds.length > 0 ? this.filterSelectedTagIds : this.selectedTagIds;
    this.selectedTagIds = [...tagsToUse];
    this.selectedTagNames = this.getTagNamesByIds(this.selectedTagIds);
    this.userTags = this.getTagObjectsByIds(this.selectedTagIds);

    const tagText = this.selectedTagNames.length > 0 ? this.selectedTagNames.join(', ') : this.translate.instant('No tags selected');
    this.addUserMessage(`${this.selectedPeriod}, ${tagText}`);

    this.conversationState = 'report_generated';
    this.executeQuery(this.currentQuestion);
  }

  public onSkipPeriodAndTags(): void {
    if (this.conversationState !== 'awaiting_period') return;

    // Skip ignores any selected tags and always uses the 7-day default period
    this.selectedPeriod = 'Past 7 days';
    this.selectedTagIds = [];
    this.filterSelectedTagIds = [];
    this.selectedTagNames = [];
    this.userTags = [];

    this.addUserMessage(`${this.selectedPeriod}, ${this.translate.instant('No tags selected')}`);
    this.conversationState = 'report_generated';
    this.executeQuery(this.currentQuestion);
  }

  public onRetryWithNewFilters(): void {
    if (!this.selectedPeriod && this.lastUsedPeriod) {
      this.selectedPeriod = this.lastUsedPeriod;
    }
    if (!this.selectedPeriod) {
      this.snackBarService.failure(this.translate.instant('Please select a period'));
      return;
    }

    if (this.filterSelectedTagIds.length > 0) {
      this.selectedTagIds = [...this.filterSelectedTagIds];
    }
    this.selectedTagNames = this.getTagNamesByIds(this.selectedTagIds);
    this.userTags = this.getTagObjectsByIds(this.selectedTagIds);

    const tagText = this.selectedTagNames.length > 0 ? this.selectedTagNames.join(', ') : this.translate.instant('No tags selected');
    this.addUserMessage(`${this.selectedPeriod}, ${tagText}`);
    this.showNoDataRetrySection = false;
    this.executeQuery(this.getEffectiveQuestion(), true);
  }

  public onCustomQuestionSubmit(): void {
    const typedQuestion = this.tripDetailsForm.get('customQuestion')?.value?.trim();

    if (this.isFirstQuery && this.conversationState === 'initial') {
      if (!typedQuestion) {
        this.snackBarService.failure(this.translate.instant('Please enter a question'));
        return;
      }
      this.currentQuestion = typedQuestion;
      this.addUserMessage(typedQuestion);
      this.conversationState = 'report_generated';
      this.executeQuery(typedQuestion);
      this.clearCustomQuestionInput();
      return;
    }

    if (!this.isFirstQuery && this.conversationState === 'report_generated') {
      if (!typedQuestion) {
        this.snackBarService.failure(this.translate.instant('Please enter a question'));
        return;
      }
      if (!this.selectedPeriod) {
        this.snackBarService.failure(this.translate.instant('Please ensure period is selected'));
        return;
      }
      this.currentQuestion = typedQuestion;
      this.addUserMessage(typedQuestion);
      this.sql_statements = [];
      this.executeQuery(typedQuestion);
      this.clearCustomQuestionInput();
    }
  }

  public selectDateRangeFromFilter(days: string): void {
    this.selectedPeriod = days;
    this.isExplicitPeriodSelection = true;
    const numDays = DATE_RANGE_PRESET_TO_DAYS[days as keyof typeof DATE_RANGE_PRESET_TO_DAYS];
    this.selectedMentions.dateRange = numDays ? { days: numDays } : null;
    this.visualsAlreadyGenerated = false;
    this.insightsAlreadyGenerated = false;
    this.executeQuery(this.getEffectiveQuestion());
  }

  public selectedTags(tags: number[]): void {
    this.selectedTagIds = tags;
    this.filterSelectedTagIds = tags;
    this.assignedTagList = tags;
    this.selectedTagNames = this.getTagNamesByIds(tags);
    this.visualsAlreadyGenerated = false;
    this.insightsAlreadyGenerated = false;
  }

  public selectedTagsRightSection(tags: number[]): void {
    this.visualsAlreadyGenerated = false;
    this.insightsAlreadyGenerated = false;
    this.pendingTagSelection = [...tags];
    this.filterSelectedTagIds = [...tags];
    this.selectedTagNames = this.getTagNamesByIds(tags);
  }

  public onTagDropdownClosed(): void {
    if (this.pendingTagSelection === null) return;

    const newTagIds = [...this.pendingTagSelection];
    const previousTagSet = new Set(this.selectedTagIds);
    const tagsChanged = previousTagSet.size !== newTagIds.length || newTagIds.some((id) => !previousTagSet.has(id));
    const questionToUse = this.getEffectiveQuestion();

    if (tagsChanged && (questionToUse || this.isEditMode) && this.conversationState === 'report_generated') {
      this.selectedTagIds = [...newTagIds];
      this.assignedTagList = [...newTagIds];
      this.selectedTagNames = this.getTagNamesByIds(newTagIds);
      this.userTags = this.getTagObjectsByIds(newTagIds);

      if (newTagIds.length === 0) {
        this.unSelectAllTag = true;
        setTimeout(() => {
          this.unSelectAllTag = false;
        }, 100);
      }

      this.currentQuestion = questionToUse;
      this.executeQuery(questionToUse);
    }

    this.pendingTagSelection = null;
  }

  public onGenerateInsights(): void {
    if (this.isGeneratingInsights) return;
    if (this.isWorkflowAwaitingInput) return;
    if (!this.dataSource?.length) {
      this.snackBarService.failure(this.translate.instant('No data available to generate insights'));
      return;
    }
    this.showInsightsModal = true;
    this.insightsAlreadyGenerated = true;
  }

  public onGenerateVisuals(): void {
    this.selectedTabIndex = 1;
    this.switchCanvasView(1);
  }

  public onInsightsModalCancel(): void {
    this.showInsightsModal = false;
  }

  public onInsightsModalProceed(): void {
    this.isGeneratingInsights = true;
    const sqlData = this.getSqlPayloadForLlm(this.getCurrentSqlStatements());

    const crossDomainPlan = this.getCurrentCrossDomainPlan();
    const requestBody = {
      clientId: this.clientId,
      sql: sqlData,
      domain: this.getCurrentDomain(),
      ...(crossDomainPlan ? { crossDomainPlan } : {}),
      ...(this.hasValidReportSourceId ? { reportSourceId: this.getCurrentReportSourceId() } : {}),
      fleetId: this.fleetId,
      tagIds: this.selectedTagIds.map(Number),
      sessionId: this.sessionId,
    };

    this.reportService
      .generateInsights(requestBody)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isGeneratingInsights = false;
          this.showInsightsModal = false;
        })
      )
      .subscribe({
        next: (response: any) => {
          const insightsText = response?.insights || response?.answer || '';
          if (insightsText) {
            this.addInsightsMessage(insightsText);
            this.insightsAlreadyGenerated = true;
            this.snackBarService.success(this.translate.instant('Insights generated successfully'));
          } else {
            this.addBotMessage(this.translate.instant('Unable to generate insights at this time. Please try again.'));
          }
        },
        error: () => {
          this.addBotMessage(this.translate.instant('Failed to generate insights. Please try again.'));
          this.snackBarService.failure(this.translate.instant('Failed to generate insights'));
        },
      });
  }

  public downloadAsCsv(): void {
    if (!this.dataSource?.length) return;

    const headers = this.displayedColumns;
    const rows = this.dataSource.map((row) =>
      headers
        .map((col) => {
          const val = row[col] ?? '';
          const str = String(val);
          return str.includes(',') || str.includes('"') || str.includes('\n') ? `"${str.replace(/"/g, '""')}"` : str;
        })
        .join(',')
    );
    const csv = [headers.join(','), ...rows].join('\n');

    const slug = (this.currentQuestion || 'report')
      .replace(/[^a-zA-Z0-9 ]/g, '')
      .trim()
      .replace(/\s+/g, '_')
      .substring(0, 48);
    this.reportService.downloadFile(csv, `${slug}.csv`, 'text/csv;charset=utf-8;');
  }

  public downloadReport(): void {
    if (this.isWorkflowAwaitingInput) return;

    if (!this.dataSource?.length) {
      this.snackBarService.failure(this.translate.instant('No data available to download'));
      return;
    }

    if (this.isCrossDomain) {
      this.downloadCrossDomainCsv();
      return;
    }

    this.csvLoader = true;
    const sql = this.getSqlPayloadForLlm(this.getCurrentSqlStatements());
    const requestBody: any = {
      ...(this.sessionId && { sessionId: this.sessionId }),
      sql,
      domain: this.getCurrentDomain(),
      ...(this.getCurrentCrossDomainPlan() ? { crossDomainPlan: this.getCurrentCrossDomainPlan() } : {}),
    };
    this.http
      .post(API.POST_GENERATE_REPORT_NAME, requestBody)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const reportName = response?.report_name || '';
          this.downloadReportWithName(reportName);
        },
        error: () => {
          this.csvLoader = false;
          this.snackBarService.failure(this.translate.instant('Failed to generate report name. Please try again.'));
        },
      });
  }

  private downloadCrossDomainCsv(): void {
    const columns = this.displayedColumns;
    const escape = (val: unknown): string => {
      const str = val === null || val === undefined ? '' : String(val);
      return /[",\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };

    const header = columns.map(escape).join(',');
    const rows = this.dataSource.map((row) => columns.map((col) => escape(row[col])).join(','));
    const csv = [header, ...rows].join('\n');

    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const filename =
      (this.currentQuestion || 'report')
        .replace(/[^a-zA-Z0-9 ]/g, '')
        .trim()
        .replace(/\s+/g, '_')
        .substring(0, 50) + '.csv';

    link.href = url;
    link.download = filename;
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    this.snackBarService.success(this.translate.instant('Report downloaded successfully'));
  }

  public openSaveReportModal(): void {
    if (this.isActionControlsDisabled || this.isCrossDomain) {
      return;
    }

    if (!this.dataSource.length) {
      this.snackBarService.failure(this.translate.instant('No data available to save'));
      return;
    }

    this.saveReportLoader = true;

    const requestBody = {
      ...(this.sessionId && { sessionId: this.sessionId }),
      ...(this.getCurrentSqlStatements().length && { sql: this.getSqlPayloadForLlm(this.getCurrentSqlStatements()) }),
      domain: this.getCurrentDomain(),
      ...(this.getCurrentCrossDomainPlan() ? { crossDomainPlan: this.getCurrentCrossDomainPlan() } : {}),
    };

    this.http
      .post(API.POST_GENERATE_REPORT_NAME, requestBody)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.saveReportLoader = false;
        })
      )
      .subscribe({
        next: (response: any) => {
          this.reportName = response.report_name ?? (this.isEditMode ? (this.editReport as CustomReport)?.reportName : '') ?? '';
          const today = new Date();
          const formatted = `${String(today.getDate()).padStart(2, '0')}.${String(today.getMonth() + 1).padStart(
            2,
            '0'
          )}.${today.getFullYear()}`;
          const tagText = this.selectedTagNames.length > 0 ? this.selectedTagNames.join(', ') : 'No tags';
          this.reportDescription = `${response.report_description ?? ''} Created on ${formatted} with ${tagText}.`;
          this.openSaveModal();
        },
        error: () => {
          if (this.isEditMode && this.editReport) {
            this.reportName = (this.editReport as CustomReport).reportName ?? '';
            this.reportDescription = (this.editReport as CustomReport).reportDescription ?? '';
            this.openSaveModal();
          } else {
            this.snackBarService.failure(this.translate.instant('Failed to generate report name. Please try again.'));
          }
        },
      });
  }

  private openSaveModal(): void {
    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true, cancelable: true }));
    this.showSaveReportModal = true;
  }

  public onReportSaved(): void {
    this.showSaveReportModal = false;
    this.dialogRef.close();
  }

  public startNewChat(): void {
    this.selectedTabIndex = 0;
    this.currentQuestion = '';
    this.currentAnswer = '';
    this.sessionId = '';
    this.selectedPeriod = 'Past 7 days';
    this.conversationState = 'initial';
    this.chatMessages = [];
    this.isFirstQuery = true;
    this.showNoDataMessage = true;
    this.showNoDataRetrySection = false;
    this.dataSource = [];
    this.displayedColumns = [];
    this.sql = [];
    this.executedSql = [];
    this.sql_statements = [];
    this.turns = [];
    this.metadata = { date_range: '', sql_where_date_columns: [], tag_ids: [], domain: 'trip' };
    this.isExplicitPeriodSelection = false;
    this.showSaveReportModal = false;
    this.lastUsedPeriod = '';
    this.lastUsedTagIds = [];
    this.lastUsedTagNames = [];
    this.selectedTagIds = [];
    this.filterSelectedTagIds = [];
    this.selectedTagNames = [];
    this.assignedTagList = [];
    this.isGeneratingInsights = false;
    this.showInsightsModal = false;
    this.insightsAlreadyGenerated = false;
    this.visualsAlreadyGenerated = false;
    this.isEditMode = false;
    this.editReport = null;
    this.customReportId = '';
    this.reportQuery = '';
    this.modalTitle = 'Hi! How can I help you?';
    this.pendingTagSelection = null;
    this.canvasOpen = false;
    this.canvasTitle = 'Untitled';
    this.selectedCanvasTab = 0;
    this.hasResult = false;
    this.loadingSteps = [];
    this.homeInputValue = '';
    this.selectedCategoryKey = '';
    this.selectedCategoryQuestions = [];
    this.categoryQuestionsLoading = false;
    this.atFilterQuery = '';
    this.predefinedFilterReplace = null;
    this.selectedMentions = { drivers: [], assets: [], eventTypes: [], dateRange: null };
    this.showAtFilterPanel = false;
    this.atFilterLevel = 'categories';
    this.atFilterSelectedCategory = null;
    this.atFilterItems = [];
    this.atFilterSearch = '';
    this.globalAtSearchResults = [];

    this.tripDetailsForm?.reset();
    this.durationControl?.setValue(null);
    this.unSelectAllTag = true;

    setTimeout(() => {
      this.initializePreAssignedTags();
      this.unSelectAllTag = false;
    }, 100);

    this.calculateDateRange('Past 7 days');
    this.addBotMessage(this.translate.instant('You can select a predefined question or type your own.'));
  }

  // ── v2 canvas methods ───────────────────────────────────────────────────────

  public toggleCanvas(): void {
    if (!this.canvasOpen && !this.hasResult) return;
    this.canvasOpen = !this.canvasOpen;
    if (this.canvasOpen) setTimeout(() => this.scrollToBottom(), 550);
  }

  public openCanvas(): void {
    this.canvasOpen = true;
    setTimeout(() => this.scrollToBottom(), 550);
  }

  public switchCanvasView(tab: number): void {
    this.selectedCanvasTab = tab;
    if (tab === 1) this.visualsAlreadyGenerated = true;
  }

  public onVisualisationFromTable(val: string): void {
    if (val === 'Table') {
      this.switchCanvasView(0);
    } else {
      this.currentChartType = val;
      this.switchCanvasView(1);
    }
  }

  public onVisualisationFromChart(val: string): void {
    if (val === 'Table') {
      this.switchCanvasView(0);
    }
  }

  public handleHomeInputSend(): void {
    const question = this.homeInputValue?.trim();
    if (!question) return;
    if (this.showAtFilterPanel && this.atFilterActiveInput === 'home' && this.atFilterHasNoResults) return;
    this.homeInputValue = '';
    this.triggerQuestionFlow(question);
  }

  public handleChatInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (this.isAskButtonDisabled()) return;
      this.onCustomQuestionSubmit();
    }
    if (event.key === 'Escape') {
      this.showAtFilterPanel = false;
    }
  }

  public onHomeInputChange(event?: Event): void {
    if (event?.target) {
      const el = event.target as HTMLTextAreaElement;
      el.style.height = 'auto';
      el.style.height = el.scrollHeight + 'px';
      this.homeInputValue = el.value;
    }
    const val = this.homeInputValue ?? '';
    const isPaste = (event as InputEvent)?.inputType === 'insertFromPaste';
    const cursorPos = (event?.target as HTMLTextAreaElement | null)?.selectionStart ?? val.length;
    const textBeforeCursor = val.substring(0, cursorPos);
    const atIndex = textBeforeCursor.lastIndexOf('@');
    const charAfterAt = atIndex >= 0 ? val[atIndex + 1] : undefined;
    const cursorRightAfterAt = atIndex >= 0 && cursorPos === atIndex + 1;
    const isAtTrigger = atIndex >= 0 && (isPaste || charAfterAt === undefined || charAfterAt !== ' ' || cursorRightAfterAt);
    if (isAtTrigger) {
      const rawQuery = textBeforeCursor.substring(atIndex + 1);
      const queryAfterAt = rawQuery.includes(' ') ? rawQuery.substring(0, rawQuery.indexOf(' ')) : rawQuery;
      this.atFilterActiveInput = 'home';
      this.atFilterQuery = queryAfterAt;
      if (!this.showAtFilterPanel) {
        // Fresh open — always start from categories, not a stale items/search state
        this.atFilterLevel = 'categories';
        this.atFilterSelectedCategory = null;
        this.atFilterItems = [];
        this.atFilterSearch = '';
        this.showAtFilterPanel = true;
        this.preloadGlobalAtItems();
      }
      if (queryAfterAt.trim()) {
        if (this.atFilterLevel === 'items') {
          // Already inside a category — filter those items, keep the category header
          this.atFilterSearch = queryAfterAt;
        } else {
          this.atFilterLevel = 'search';
          this.atFilterSelectedCategory = null;
          this.performGlobalSearch(queryAfterAt);
        }
      } else if (this.atFilterLevel !== 'items') {
        this.atFilterLevel = 'categories';
        this.atFilterSelectedCategory = null;
        this.atFilterSearch = '';
      } else {
        // Cleared query while in items — reset the inline filter
        this.atFilterSearch = '';
      }
      this.searchSuggestions = [];
    } else if (val.trim().length > 0) {
      this.showAtFilterPanel = false;
      this.filterSearchSuggestions(val.trim());
    } else {
      this.showAtFilterPanel = false;
      this.searchSuggestions = [];
    }
    this.activeSuggestionIndex = -1;
  }

  private filterSearchSuggestions(query: string): void {
    const q = query.toLowerCase();
    this.searchSuggestions = this.suggestionPool
      .filter((s: string) => s.toLowerCase().includes(q))
      .slice(0, 6)
      .map((text: string) => ({ text, icon: (text.includes('@') ? 'search' : 'history') as 'search' | 'history' }));
  }

  public highlightMatch(text: string, query: string): string {
    if (!query) return text;
    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return text.replace(new RegExp(`(${escaped})`, 'gi'), '<strong>$1</strong>');
  }

  public onSuggestionSelect(suggestion: { text: string; icon: string }): void {
    this.homeInputValue = suggestion.text;
    this.searchSuggestions = [];
    this.activeSuggestionIndex = -1;
  }

  public onHomeKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (this.activeSuggestionIndex >= 0 && this.searchSuggestions.length) {
        this.onSuggestionSelect(this.searchSuggestions[this.activeSuggestionIndex]);
      } else {
        this.handleHomeInputSend();
      }
      return;
    }
    if (!this.searchSuggestions.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.activeSuggestionIndex = Math.min(this.activeSuggestionIndex + 1, this.searchSuggestions.length - 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.activeSuggestionIndex = Math.max(this.activeSuggestionIndex - 1, -1);
    } else if (event.key === 'Escape') {
      this.searchSuggestions = [];
    }
  }

  public onChatInputChange(event?: Event): void {
    if (event?.target) {
      const el = event.target as HTMLTextAreaElement;
      el.style.height = 'auto';
      el.style.height = el.scrollHeight + 'px';
      this.tripDetailsForm.get('customQuestion')?.setValue(el.value, { emitEvent: false });
    }
    const value: string = this.tripDetailsForm.get('customQuestion')?.value ?? '';
    const chatIsPaste = (event as InputEvent)?.inputType === 'insertFromPaste';
    const chatCursorPos = (event?.target as HTMLTextAreaElement | null)?.selectionStart ?? value.length;
    const chatTextBeforeCursor = value.substring(0, chatCursorPos);
    const atIndex = chatTextBeforeCursor.lastIndexOf('@');
    const charAfterAt = atIndex >= 0 ? value[atIndex + 1] : undefined;
    const chatCursorRightAfterAt = atIndex >= 0 && chatCursorPos === atIndex + 1;
    const isAtTrigger = atIndex >= 0 && (chatIsPaste || charAfterAt === undefined || charAfterAt !== ' ' || chatCursorRightAfterAt);
    if (isAtTrigger) {
      const rawQuery = chatTextBeforeCursor.substring(atIndex + 1);
      const queryAfterAt = rawQuery.includes(' ') ? rawQuery.substring(0, rawQuery.indexOf(' ')) : rawQuery;
      this.atFilterActiveInput = 'chat';
      this.atFilterQuery = queryAfterAt;
      if (!this.showAtFilterPanel) {
        // Fresh open — always start from categories, not a stale items/search state
        this.atFilterLevel = 'categories';
        this.atFilterSelectedCategory = null;
        this.atFilterItems = [];
        this.atFilterSearch = '';
        this.showAtFilterPanel = true;
        this.preloadGlobalAtItems();
      }
      if (queryAfterAt.trim()) {
        if (this.atFilterLevel === 'items') {
          // Already inside a category — filter those items, keep the category header
          this.atFilterSearch = queryAfterAt;
        } else {
          this.atFilterLevel = 'search';
          this.atFilterSelectedCategory = null;
          this.performGlobalSearch(queryAfterAt);
        }
      } else if (this.atFilterLevel !== 'items') {
        this.atFilterLevel = 'categories';
        this.atFilterSelectedCategory = null;
        this.atFilterSearch = '';
      } else {
        // Cleared query while in items — reset the inline filter
        this.atFilterSearch = '';
      }
    } else {
      this.showAtFilterPanel = false;
    }
  }

  public onAtCategorySelect(category: { label: string; icon: string; value: string }): void {
    this.atFilterSelectedCategory = category;
    this.atFilterLevel = 'items';
    this.atFilterItems = [];
    this.atFilterSearch = this.atFilterQuery;
    this.atFilterItemsLoading = true;
    this.loadAtFilterItems(category.value);
  }

  public onAtGoBack(): void {
    this.atFilterLevel = 'categories';
    this.atFilterSelectedCategory = null;
    this.atFilterItems = [];
    this.atFilterSearch = '';
  }

  public onAtItemSelect(item: MentionItem & { categoryValue?: string }): void {
    if (this.predefinedFilterReplace && this.atFilterActiveInput === 'home') {
      this.homeInputValue = this.homeInputValue.replace(this.predefinedFilterReplace, `@${item.label}`);
      this.predefinedFilterReplace = null;
      this.collectMention(item);
      const nextTokenMatch = this.homeInputValue.match(/@(asset|device|event|driver|tag)\b/i);
      if (nextTokenMatch) {
        const nextToken = nextTokenMatch[1].toLowerCase();
        const nextCategoryValue = this.PREDEFINED_TOKEN_CATEGORY_MAP[nextToken];
        const nextCategory = this.atFilterCategories.find((c) => c.value === nextCategoryValue);
        if (nextCategory) {
          this.predefinedFilterReplace = `@${nextToken}`;
          this.atFilterQuery = '';
          this.atFilterSearch = '';
          this.showAtFilterPanel = true;
          this.onAtCategorySelect(nextCategory);
          return;
        }
      }

      this.showAtFilterPanel = false;
      this.atFilterLevel = 'categories';
      this.atFilterSelectedCategory = null;
      setTimeout(() => this.homeInput?.nativeElement?.focus());
      return;
    }
    this.insertAtValue(item.label);
    this.collectMention(item);
    this.showAtFilterPanel = false;
    this.atFilterLevel = 'categories';
    this.atFilterSelectedCategory = null;
    if (this.atFilterActiveInput === 'home') {
      setTimeout(() => this.homeInput?.nativeElement?.focus());
    }
  }

  private collectMention(item: MentionItem & { categoryValue?: string }): void {
    const kind = item.kind ?? this.categoryToKind(item.categoryValue);
    switch (kind) {
      case 'driver':
        if (item.id && !this.selectedMentions.drivers.some((d) => d.id === item.id)) {
          this.selectedMentions.drivers.push({ id: item.id, name: item.label });
        }
        break;
      case 'asset':
        if (item.assetid && !this.selectedMentions.assets.some((a) => a.assetid === item.assetid)) {
          this.selectedMentions.assets.push({ assetid: item.assetid, name: item.label });
        }
        break;
      case 'safety_event':
      case 'health_event':
        if (!this.selectedMentions.eventTypes.includes(item.label)) {
          this.selectedMentions.eventTypes.push(item.label);
        }
        break;
      case 'date':
        if (item.days !== undefined) {
          this.selectedMentions.dateRange = { days: item.days };
        }
        break;
    }
  }

  private categoryToKind(categoryValue?: string): MentionItem['kind'] | undefined {
    const map: Record<string, MentionItem['kind']> = {
      drivers: 'driver',
      assets: 'asset',
      safety_events: 'safety_event',
      health_events: 'health_event',
      date: 'date',
    };
    return categoryValue ? map[categoryValue] : undefined;
  }

  private buildMentionsPayload(): Record<string, unknown> {
    const { drivers, assets, eventTypes, dateRange } = this.selectedMentions;
    return {
      ...(dateRange && { dateRange }),
      ...(drivers.length && { drivers }),
      ...(assets.length && { assets }),
      ...(eventTypes.length && { eventTypes }),
    };
  }

  private insertAtValue(value: string): void {
    if (this.atFilterActiveInput === 'home') {
      const current = this.homeInputValue ?? '';
      const atIndex = current.lastIndexOf('@');
      this.homeInputValue = (atIndex >= 0 ? current.substring(0, atIndex) : current) + `@${value}`;
    } else {
      const current: string = this.tripDetailsForm.get('customQuestion')?.value ?? '';
      const atIndex = current.lastIndexOf('@');
      this.tripDetailsForm.get('customQuestion')?.setValue((atIndex >= 0 ? current.substring(0, atIndex) : current) + `@${value}`);
    }
  }

  private loadAtFilterItems(category: string): void {
    switch (category) {
      case 'date':
        this.atFilterItems = this.buildDateMentionItems();
        this.atFilterItemsLoading = false;
        break;

      case 'assets':
        this.http
          .get<any>(API.GET_FLEET_ASSETS, { params: { limit: '1000', offset: '0' } })
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res) => {
              const list: any[] = res?.data?.assets ?? res?.assets ?? [];
              this.atFilterItems = list
                .filter((a: any) => a.assetName || a.assetId)
                .map((a: any) => ({
                  label: a.assetName ?? a.assetId,
                  icon: 'local_shipping',
                  fontSet: 'material-symbols-outlined',
                  kind: 'asset' as const,
                  assetid: String(a.assetId ?? a.assetid ?? ''),
                }));
              this.atFilterItemsLoading = false;
            },
            error: () => {
              this.atFilterItemsLoading = false;
            },
          });
        break;

      case 'drivers':
        this.http
          .get<any>(API.GET_UNIFIED_DRIVERS, { params: { limit: '1000', offset: '0' } })
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res) => {
              const list = res?.rows ?? res?.data ?? res ?? [];
              this.atFilterItems = (Array.isArray(list) ? list : []).map((d: any) => ({
                label: (d.driverName || `${d.firstName ?? ''} ${d.lastName ?? ''}`.trim() || d.driverId || '').toString(),
                icon: 'person',
                fontSet: 'material-symbols-outlined',
                kind: 'driver' as const,
                id: String(d.driverId ?? d.id ?? ''),
              }));
              this.atFilterItemsLoading = false;
            },
            error: () => {
              this.atFilterItemsLoading = false;
            },
          });
        break;

      case 'safety_events': {
        this.http
          .get<{ standardEvents: any[]; customExternalEvents: any[] }>(API.GET_FLEET_EVENTS_CUSTOM_EVENTS)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res) => {
              const featureFlags = this.durationService.planDetails?.featureFlags ?? [];
              const standard: any[] = (res?.standardEvents ?? []).filter((e: any) => e.state === 'ENABLED');
              const custom: any[] = featureFlags.includes('custom_events')
                ? (res?.customExternalEvents ?? []).filter((e: any) => e.state === 'ENABLED')
                : [];
              this.atFilterItems = [...standard, ...custom].map((e: any) => ({
                label: e.eventName || e.eventType,
                icon: 'car_crash',
                fontSet: 'material-symbols-outlined',
                kind: 'safety_event' as const,
              }));
              this.atFilterItemsLoading = false;
            },
            error: () => {
              this.atFilterItemsLoading = false;
            },
          });
        break;
      }

      case 'health_events':
        this.http
          .get<any>(API.GET_FLEET_HEALTH_Agg)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res) => {
              const categoryAggregate: Record<string, number> = res?.categoryAggregate ?? {};
              this.atFilterItems = Object.keys(categoryAggregate).map((key) => ({
                label: this.pascalToReadable(key),
                icon: 'favorite_border',
                kind: 'health_event' as const,
              }));
              this.atFilterItemsLoading = false;
            },
            error: () => {
              this.atFilterItemsLoading = false;
            },
          });
        break;

      default:
        this.atFilterItemsLoading = false;
    }
  }

  private buildDateMentionItems(): MentionItem[] {
    const presets: { label: string; days: number }[] = [
      { label: 'Today', days: 1 },
      { label: 'Last 7 days', days: 7 },
      { label: 'Last 30 days', days: 30 },
      { label: 'Last 90 days', days: 90 },
    ];
    return presets.map((p) => ({ label: p.label, icon: 'event', fontSet: 'material-icons', kind: 'date' as const, days: p.days }));
  }

  public closeAtFilterPanel(): void {
    this.showAtFilterPanel = false;
    this.atFilterLevel = 'categories';
    this.atFilterSelectedCategory = null;
    this.atFilterQuery = '';
    this.globalAtSearchResults = [];
  }

  private preloadGlobalAtItems(): void {
    this.globalAtCache['date'] = this.buildDateMentionItems();

    if (!this.globalAtCache['assets']) {
      this.http
        .get<any>(API.GET_FLEET_ASSETS)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            const list: any[] = res?.data?.assets ?? res?.assets ?? [];
            this.globalAtCache['assets'] = list
              .filter((a: any) => a.assetName || a.assetId)
              .map((a: any) => ({
                label: a.assetName ?? a.assetId,
                icon: 'local_shipping',
                fontSet: 'material-symbols-outlined',
                kind: 'asset' as const,
                assetid: String(a.assetId ?? a.assetid ?? ''),
              }));
            this.updateGlobalSearchResults();
          },
          error: () => {
            this.globalAtCache['assets'] = [];
          },
        });
    }

    if (!this.globalAtCache['drivers']) {
      this.http
        .get<any>(API.GET_UNIFIED_DRIVERS)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            const list = res?.rows ?? res?.data ?? res ?? [];
            this.globalAtCache['drivers'] = (Array.isArray(list) ? list : []).map((d: any) => ({
              label: (d.driverName || `${d.firstName ?? ''} ${d.lastName ?? ''}`.trim() || d.driverId || '').toString(),
              icon: 'person',
              fontSet: 'material-symbols-outlined',
              kind: 'driver' as const,
              id: String(d.driverId ?? d.id ?? ''),
            }));
            this.updateGlobalSearchResults();
          },
          error: () => {
            this.globalAtCache['drivers'] = [];
          },
        });
    }

    if (!this.globalAtCache['safety_events']) {
      const now = new Date();
      const past = new Date(now);
      past.setDate(past.getDate() - 30);
      const endDate = now.toISOString().split('T')[0];
      const startDate = past.toISOString().split('T')[0];
      this.http
        .get<any>(API.GET_VIOLATIONS, { params: { startDate, endDate } })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            const rows: any[] = res?.rows ?? [];
            const uniqueTypes = [...new Set(rows.map((r: any) => r.eventType).filter(Boolean))] as string[];
            this.globalAtCache['safety_events'] = uniqueTypes.map((t) => ({
              label: t,
              icon: 'car_crash',
              fontSet: 'material-symbols-outlined',
              kind: 'safety_event' as const,
            }));
            this.updateGlobalSearchResults();
          },
          error: () => {
            this.globalAtCache['safety_events'] = [];
          },
        });
    }
  }

  private updateGlobalSearchResults(): void {
    if (this.atFilterLevel === 'search' && this.atFilterQuery.trim()) {
      this.performGlobalSearch(this.atFilterQuery);
    }
  }

  private performGlobalSearch(query: string): void {
    const q = query.toLowerCase().trim();
    if (!q) {
      this.globalAtSearchResults = [];
      return;
    }
    const results: {
      label: string;
      icon: string;
      fontSet?: string;
      categoryValue: string;
      categoryLabel: string;
      categoryIcon: string;
      categoryFontSet?: string;
    }[] = [];
    for (const cat of this.atFilterCategories) {
      const cached = this.globalAtCache[cat.value];
      if (!cached) continue;
      for (const item of cached) {
        if (item.label.toLowerCase().includes(q)) {
          results.push({
            ...item,
            categoryValue: cat.value,
            categoryLabel: cat.label,
            categoryIcon: cat.icon,
            categoryFontSet: cat.fontSet,
          });
        }
      }
    }
    this.globalAtSearchResults = results;
  }

  private pascalToReadable(key: string): string {
    // SDCardNeedFormatting → SD Card Need Formatting
    // DeviceOffline → Device Offline
    return key
      .replace(/([a-z])([A-Z])/g, '$1 $2') // insert space between lower→upper
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2') // insert space in consecutive caps: SDCard → SD Card
      .trim();
  }

  public openCanvasFromFileCard(): void {
    this.openCanvas();
    this.selectedCanvasTab = 0;
  }

  private triggerQuestionFlow(question: string): void {
    if (!this.isFirstQuery || this.conversationState !== 'initial') return;
    this.currentQuestion = question;
    this.addUserMessage(question);
    this.conversationState = 'report_generated';
    this.executeQuery(question);
  }

  private _loadingTimers: ReturnType<typeof setTimeout>[] = [];

  private startLoadingAnimation(): void {
    this._loadingTimers.forEach(clearTimeout);
    this._loadingTimers = [];
    this.loadingSteps = [
      { label: 'Interpreting question', state: 'queued' },
      { label: 'Generating query', state: 'queued' },
      { label: 'Querying database', state: 'queued' },
      { label: 'Rendering report', state: 'queued' },
    ];
    this._loadingTimers.push(
      setTimeout(() => {
        if (this.loadingSteps[0]) this.loadingSteps[0].state = 'active';
      }, 300)
    );
    this._loadingTimers.push(
      setTimeout(() => {
        if (this.loadingSteps[0]) this.loadingSteps[0].state = 'done';
        if (this.loadingSteps[1]) this.loadingSteps[1].state = 'active';
      }, 1800)
    );
    this._loadingTimers.push(
      setTimeout(() => {
        if (this.loadingSteps[1]) this.loadingSteps[1].state = 'done';
        if (this.loadingSteps[2]) this.loadingSteps[2].state = 'active';
      }, 3200)
    );
  }

  private finishLoadingAnimation(): void {
    this._loadingTimers.forEach(clearTimeout);
    this._loadingTimers = [];
    this.loadingSteps.forEach((s) => (s.state = 'done'));
  }

  private buildFileCard(question: string): ChatMessage['fileCard'] {
    const displayName = (question || 'report').trim().replace(/\s+/g, ' ');
    const tagsLabel =
      this.selectedTagNames?.length > 0
        ? this.selectedTagNames.length === 1
          ? this.selectedTagNames[0]
          : `${this.selectedTagNames[0]} (+${this.selectedTagNames.length - 1} more)`
        : '';
    return {
      name: displayName,
      rows: this.dataSource.length,
      cols: this.displayedColumns.length,
      dateRange: this.isExplicitPeriodSelection ? this.selectedPeriod : this.metadata.date_range || this.selectedPeriod,
      tagsLabel,
    };
  }

  // ── end v2 methods ───────────────────────────────────────────────────────────

  private clearCustomQuestionInput(): void {
    this.tripDetailsForm.get('customQuestion')?.setValue('');
  }

  private scrollToBottom(): void {
    try {
      if (this.chatContainer) {
        this.chatContainer.nativeElement.scrollTop = this.chatContainer.nativeElement.scrollHeight;
      }
    } catch (err) {
      console.error('Scroll error:', err);
    }
  }

  private addUserMessage(content: string): void {
    const renderedContent = this.formatQuestionHtml(content);
    this.chatMessages.push({ type: 'user', content, renderedContent, timestamp: new Date() });
    setTimeout(() => this.scrollToBottom(), 100);
  }

  private addBotMessage(content: string, options?: { isPeriodSelection?: boolean; requestId?: string }): void {
    let displayContent = content;
    try {
      const parsed = JSON.parse(content);
      if (parsed && typeof parsed.answer === 'string') {
        displayContent = parsed.answer;
      }
    } catch {
      /* plain text */
    }

    const html = marked.parse(displayContent) as string;
    const renderedContent = this.sanitizer.bypassSecurityTrustHtml(html);
    this.chatMessages.push({ type: 'bot', content: displayContent, renderedContent, timestamp: new Date(), ...options });
    setTimeout(() => this.scrollToBottom(), 100);
  }

  public onCandidateSelect(candidate: ResolutionCandidate): void {
    const resolutionMsg = this.chatMessages
      .slice()
      .reverse()
      .find((m) => m.type === 'resolution');
    if (resolutionMsg) resolutionMsg.candidates = [];

    const label =
      candidate.label ??
      candidate.driver_name ??
      candidate.asset_name ??
      candidate.assetid ??
      candidate.deviceid ??
      candidate.driver_id ??
      '';
    if (!label) return;
    this.addUserMessage(label);

    // Picker path: merge candidate.mention into selectedMentions and re-POST originalQuestion
    if (candidate.mention) {
      candidate.mention.drivers?.forEach((d) => {
        if (!this.selectedMentions.drivers.some((x) => x.id === d.id)) {
          this.selectedMentions.drivers.push(d);
        }
      });
      candidate.mention.assets?.forEach((a) => {
        if (!this.selectedMentions.assets.some((x) => x.assetid === a.assetid)) {
          this.selectedMentions.assets.push(a);
        }
      });
    }

    const questionToSend = resolutionMsg?.originalQuestion || label;
    this.executeQuery(questionToSend);
  }

  public hasDuplicateLabel(candidate: ResolutionCandidate, candidates: ResolutionCandidate[]): boolean {
    const label = candidate.label || candidate.driver_name || candidate.asset_name;
    if (!label) return false;
    let count = 0;
    for (const c of candidates) {
      const cLabel = c.label || c.driver_name || c.asset_name;
      if (cLabel === label && ++count > 1) return true;
    }
    return false;
  }

  public getCandidateId(candidate: ResolutionCandidate): string {
    return candidate.driver_id || candidate.assetid || candidate.deviceid || '';
  }

  private addInsightsMessage(content: string): void {
    const html = marked.parse(content) as string;
    const safeHtml = this.sanitizer.bypassSecurityTrustHtml(html);
    this.chatMessages.push({ type: 'insights', content, renderedContent: safeHtml, timestamp: new Date() });
  }

  private addSqlThinkingMessage(sqlData: string): void {
    const markdown = '```sql\n' + sqlData + '\n```';
    const html = marked.parse(markdown) as string;
    const renderedContent = this.sanitizer.bypassSecurityTrustHtml(html);
    this.chatMessages.push({
      type: 'sql',
      content: '',
      timestamp: new Date(),
      sqlData,
      renderedContent,
      showSqlExpanded: false,
    });
    setTimeout(() => this.scrollToBottom(), 100);
  }

  private getEffectiveQuestion(): string {
    return (
      this.currentQuestion?.trim() || this.editReport?.reportPayload?.question?.trim() || (this.editReport as any)?.reportName?.trim() || ''
    );
  }

  private calculateDateRange(range: string): void {
    const today = new Date();
    const daysMap: Record<string, number> = {
      'Past 3 days': 3,
      'Past 7 days': 7,
      'Past 14 days': 14,
      'Past 30 days': 30,
    };
    const offset = daysMap[range] ?? 30;
    today.setDate(today.getDate() - offset);
  }

  private loadSuggestionPool(): void {
    this.http
      .get<{ questions: { category: string; question: string }[] }>(API.GET_SUGGESTED_QUESTIONS)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.suggestionPool = (res?.questions ?? []).map((q) => q.question).filter(Boolean);
        },
        error: () => {},
      });
  }

  private loadAttributeList(): void {
    this.taggingService
      .getAttributes({ limit: 500, offset: 0, 'entityNames[]': this.entityType })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.attributeList = res?.data ?? [];
          const { fleets = [] } = this.accessService.getLoginInfo();
          const fleet = fleets.find((x: any) => x.fleetId === this.fleetId);
          const userAttrIds = [...new Set((fleet?.userTags ?? []).map((t: any) => Number(t.attributeId)))];
          const userTagIds = (fleet?.userTags ?? []).map((t: any) => Number(t.tagId));
          this.allTags = this.extractAllTags(
            this.attributeList,
            userAttrIds.length ? userAttrIds : undefined,
            userTagIds.length ? userTagIds : undefined
          );
          this.initializePreAssignedTags();
        },
        error: () => {},
      });
  }

  private extractAllTags(attributes: any[], userAttrIds?: number[], userTagIds?: number[]): Tag[] {
    if (!Array.isArray(attributes)) return [];
    const seen = new Set<number>();
    return attributes.flatMap((attr) => {
      const tags: any[] = attr.tags ?? [];
      let filtered: any[];

      if (attr.attributeType === 'ACCESS' && userAttrIds?.length) {
        if (!userAttrIds.includes(Number(attr.attributeId))) return [];
        filtered = userTagIds?.length ? tags.filter((tag: any) => userTagIds.includes(Number(tag.tagId))) : tags;
      } else {
        filtered = tags;
      }

      return filtered
        .filter((tag: any) => {
          const id = Number(tag.tagId);
          if (seen.has(id)) return false;
          seen.add(id);
          return true;
        })
        .map((tag: any) => ({
          ...tag,
          attributeId: attr.attributeId,
          attributeName: attr.attributeName,
          attributeType: attr.attributeType,
        }));
    });
  }

  private getTagNamesByIds(tagIds: number[]): string[] {
    if (!tagIds?.length) return [];
    return tagIds.reduce<string[]>((acc, tagId) => {
      const found = this.allTags.find((t) => t.tagId === tagId) ?? this.userTags.find((t) => t.id === tagId || t.tagId === tagId);
      const name = found?.tagName ?? (found as any)?.name;
      if (name) acc.push(name);
      return acc;
    }, []);
  }

  private getTagObjectsByIds(tagIds: number[]): Tag[] {
    if (!tagIds?.length) return [];
    return tagIds.reduce<Tag[]>((acc, tagId) => {
      const fromAll = this.allTags.find((t) => t.tagId === tagId);
      if (fromAll) {
        acc.push(fromAll);
        return acc;
      }

      const fromUser = this.userTags.find((t) => t.id === tagId || t.tagId === tagId);
      if (fromUser) {
        acc.push({
          ...fromUser,
          tagId: fromUser.id ?? fromUser.tagId,
          tagName: (fromUser as any).name ?? fromUser.tagName,
        });
      }
      return acc;
    }, []);
  }

  private initializePreAssignedTags(): void {
    const { fleets = [] } = this.accessService.getLoginInfo();
    const fleet = fleets.find((x: any) => x.fleetId === this.fleetId);
    const preAssigned: number[] = fleet?.userTags?.map((t: any) => t.tagId) ?? [];

    this.selectedTagIds = [...preAssigned];
    this.filterSelectedTagIds = [...preAssigned];
    this.lastUsedTagIds = [...preAssigned];
    this.selectedTagNames = this.getTagNamesByIds(preAssigned);
    this.lastUsedTagNames = [...this.selectedTagNames];
    this.userTags = this.getTagObjectsByIds(preAssigned);
    this.assignedTagList = [...preAssigned];
  }

  private executeQuery(question: string, isFilterChange = false): void {
    if (!this.selectedPeriod) {
      this.snackBarService.failure(this.translate.instant('Please select a report period'));
      return;
    }

    this.lastUsedPeriod = this.selectedPeriod;
    this.lastUsedTagIds = [...this.selectedTagIds];
    this.lastUsedTagNames = [...this.selectedTagNames];
    const metadataBeforeQuery = { ...this.metadata };
    this.metadata = { ...this.metadata, date_range: '' };
    this.isLoading = true;
    this.showNoDataMessage = false;
    this.showNoDataRetrySection = false;
    this.chatMessages.forEach((m) => (m.showRetryCard = false));
    this.insightsAlreadyGenerated = false;
    this.dataSource = [];
    this.displayedColumns = [];
    this.canvasTitle = question.replace(/@\w+/g, '').trim().substring(0, 60) || 'Untitled';
    this.startLoadingAnimation();

    const mentions = this.buildMentionsPayload();
    const resetContext = this.isEditMode && this.isFirstEditQuestion;
    this.isFirstEditQuestion = false;
    const baseBody: any = {
      question: question,
      clientId: this.clientId,
      fleetId: this.fleetId,
      tagIds: this.selectedTagIds,
      domain: this.getCurrentDomain(),
      ...(Object.keys(mentions).length > 0 && { mentions }),
      ...(resetContext && { resetContext: true }),
      ...(this.hasValidReportSourceId ? { reportSourceId: this.getCurrentReportSourceId() } : {}),
      ...(this.sessionId && { sessionId: this.sessionId }),
    };

    const hasFollowUpSql = !this.isFirstQuery && !!this.sessionId && this.sql_statements?.length > 0;

    // Pure filter/tag changes → edit-query (SQL rewrite only, no NL processing)
    // All natural-language queries (new, follow-up, edit) → talk-to-data
    const request$ = isFilterChange
      ? this.http.post<CustomReportsResponse>(API.POST_EDIT_QUERY, { ...baseBody, sql: this.sql_statements[0] })
      : this.reportService.getCustomReports({
          ...baseBody,
          // Provide latest SQL as context for follow-ups and edit-mode edits
          ...(hasFollowUpSql && { sql: this.sql_statements[0] }),
          ...(this.isEditMode && !hasFollowUpSql && { sql: this.getSqlPayloadForLlm(this.toSqlArray(this.data?.sql)) }),
          // Durable conversation context for saved-report edits
          ...(this.isEditMode && this.customReportId && { customReportId: this.customReportId }),
          ...(this.isEditMode && { reportMetadata: metadataBeforeQuery }),
        });

    request$
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: (response: CustomReportsResponse) => {
          if (response?.sessionId) this.sessionId = response.sessionId;

          this.sql = response?.sql_statements ?? [];
          this.executedSql = response?.executed_sql_statements ?? [];
          this.sql_statements = response?.sql_statements ?? [];
          const responseMetadata = response?.metadata as Partial<Metadata> | undefined;
          this.metadata = responseMetadata
            ? {
                ...responseMetadata,
                date_range: responseMetadata.date_range ?? '',
                sql_where_date_columns: responseMetadata.sql_where_date_columns ?? [],
                tag_ids: responseMetadata.tag_ids ?? [],
                domain: responseMetadata.domain ?? 'trip',
              }
            : { date_range: '', sql_where_date_columns: [], tag_ids: [], domain: 'trip' };

          if (!isFilterChange && this.metadata.date_range) {
            const matchedPeriod = this.reportPeriods.find(
              (p: any) => p.label === this.metadata.date_range || p.days === this.metadata.date_range
            );
            if (!matchedPeriod) {
              const customEntry = { label: this.metadata.date_range, days: this.metadata.date_range };
              this.reportPeriods = [customEntry, ...TALK_TO_DATA_DATE_RANGES];
            } else {
              this.reportPeriods = TALK_TO_DATA_DATE_RANGES;
            }
            this.selectedPeriod = matchedPeriod ? matchedPeriod.days ?? matchedPeriod.label : this.metadata.date_range;
          }

          if (response.sql_statements?.length) {
            const tqi = (response.metadata as Metadata)?.tableQueryIndex ?? 0;
            const sqlToShow = response.sql_statements[tqi] ?? response.sql_statements[0];
            this.addSqlThinkingMessage(sqlToShow);
          }
          this.processResponse(response);
          if (!response.needsResolution) {
            this.turns.push({
              question,
              answer: response.answer ?? '',
              sql: response.sql_statements?.[0] ?? '',
              executed_sql: response.executed_sql_statements?.[0] ?? '',
              mentions: mentions as Record<string, unknown>,
              requestId: (response as any).requestId,
              metadata: this.metadata,
            });
          }
          this.isFirstQuery = false;
          this.conversationState = 'report_generated';
        },
        error: (err) => {
          this.handleError(err);
          this.isFirstQuery = false;
          this.conversationState = 'report_generated';
        },
      });
  }

  private processResponse(response: CustomReportsResponse): void {
    if (!response) {
      this.handleNoData();
      return;
    }

    if (response.sessionId) this.sessionId = response.sessionId;

    if (response.needsResolution && response.candidates?.length) {
      this.showNoDataMessage = true;
      this.dataSource = [];
      this.displayedColumns = [];
      const html = marked.parse(response.answer ?? '') as string;
      const safeHtml = this.sanitizer.bypassSecurityTrustHtml(html);
      this.chatMessages.push({
        type: 'resolution',
        content: response.answer ?? '',
        renderedContent: safeHtml,
        candidates: response.candidates,
        originalQuestion: response.originalQuestion,
        timestamp: new Date(),
      });
      setTimeout(() => this.scrollToBottom(), 100);
      return;
    }

    this.sql = response.sql_statements ?? [];
    this.currentAnswer = response.answer ?? '';
    this.addBotMessage(this.currentAnswer, { requestId: (response as any).requestId });

    // Pin resolvedMentions so follow-up turns carry exact IDs (no re-clarification)
    const resolved = response.metadata?.resolvedMentions;
    if (resolved) {
      resolved.drivers?.forEach((d) => {
        if (!this.selectedMentions.drivers.some((x) => x.id === d.id)) {
          this.selectedMentions.drivers.push(d);
        }
      });
      resolved.assets?.forEach((a) => {
        if (!this.selectedMentions.assets.some((x) => x.assetid === a.assetid)) {
          this.selectedMentions.assets.push(a);
        }
      });
    }

    if (this.isWorkflowAwaitingInput) {
      this.showNoDataRetrySection = false;
      this.showNoDataMessage = false;
      this.dataSource = [];
      this.displayedColumns = [];
      return;
    }

    const tqi = (response.metadata as Metadata)?.tableQueryIndex ?? 0;
    let tableData = response.data?.[tqi];

    // If the designated table is empty, fall back to the first non-empty table in the response
    if ((!Array.isArray(tableData) || tableData.length === 0 || !this.checkIfMeaningfulData(tableData)) && Array.isArray(response.data)) {
      const fallback = response.data.find((d) => Array.isArray(d) && d.length > 0 && this.checkIfMeaningfulData(d));
      if (fallback) tableData = fallback;
    }

    if (Array.isArray(tableData) && tableData.length > 0 && this.checkIfMeaningfulData(tableData)) {
      const processed = tableData.map((row) => {
        const r = { ...row };
        const key = 'Average Trip Duration (sec)';
        if (key in r && typeof r[key] === 'number') {
          r[key] = Math.round((r[key] as number) * 100) / 100;
        }
        return r;
      });

      this.displayedColumns = Object.keys(processed[0]);
      this.dataSource = processed;
      if ((this.dataSource.length > 20 || this.dataSource.length <= 2) && this.selectedCanvasTab === 1) {
        this.selectedCanvasTab = 0;
      }
      this.showNoDataMessage = false;
      this.showNoDataRetrySection = false;
      this.hasResult = true;
      this.finishLoadingAnimation();

      const answerMsg = this.chatMessages
        .slice()
        .reverse()
        .find((m) => m.type === 'bot' && !m.isPeriodSelection);
      if (answerMsg) {
        answerMsg.fileCard = this.buildFileCard(this.currentQuestion);
      }
      setTimeout(() => this.openCanvas(), 400);

      if (!this.isGeneratingInsights) {
        this.snackBarService.success(this.translate.instant('Report generated successfully'));
      }
    } else {
      this.handleNoData();
    }
  }

  private checkIfMeaningfulData(tableData: any[]): boolean {
    if (tableData.length > 1) return true;
    return Object.values(tableData[0]).some((v) => v !== null && v !== undefined && v !== '');
  }

  private handleNoData(): void {
    if (this.isWorkflowAwaitingInput) {
      this.showNoDataRetrySection = false;
      this.showNoDataMessage = false;
      return;
    }

    this.showNoDataMessage = true;
    this.showNoDataRetrySection = true;
    this.showPeriodExpanded = false;
    this.showTagExpanded = false;
    this.dataSource = [];
    this.displayedColumns = [];
    this.restoreLastUsedFilters();
    this.addBotMessage(
      this.translate.instant('Currently there is no activity found for the selected values, kindly select a different value and try again:')
    );
    setTimeout(() => this.openCanvas(), 400);
  }

  private handleError(error: HttpErrorResponse): void {
    this.showNoDataMessage = true;
    this.showNoDataRetrySection = true;
    this.showPeriodExpanded = false;
    this.showTagExpanded = false;
    this.dataSource = [];
    this.displayedColumns = [];
    this.restoreLastUsedFilters();

    const statusMessages: Record<number, string> = {
      400: 'Invalid request. Please check your query and try again.',
      401: 'Unauthorized. Please log in again.',
      404: 'Service not found. Please contact support.',
      500: 'Unable to fetch data.',
    };
    const msg = statusMessages[error.status] ?? error.error?.message ?? 'Failed to generate report. Please try again.';

    this.addBotMessage(this.translate.instant(msg));
    this.snackBarService.failure(this.translate.instant(msg));
    setTimeout(() => this.openCanvas(), 400);
  }

  private restoreLastUsedFilters(): void {
    if (this.lastUsedTagIds.length > 0) {
      this.selectedTagIds = [...this.lastUsedTagIds];
      this.assignedTagList = this.getTagObjectsByIds(this.lastUsedTagIds) as any;
      this.selectedTagNames = [...this.lastUsedTagNames];
      this.userTags = this.getTagObjectsByIds(this.lastUsedTagIds);
    } else {
      this.selectedTagIds = [];
      this.selectedTagNames = [];
      this.assignedTagList = [];
      this.userTags = [];
    }
    if (this.lastUsedPeriod) this.selectedPeriod = this.lastUsedPeriod;
  }

  private downloadReportWithName(reportName: string): void {
    const tqi = this.metadata?.tableQueryIndex ?? 0;
    let reportQuery = '';
    if (this.isEditMode) {
      reportQuery = this.data?.reportQuery ?? this.data?.sql?.[tqi] ?? this.data?.sql?.[0] ?? '';
    } else if (this.executedSql && this.executedSql.length > 0) {
      reportQuery = this.executedSql[tqi] ?? this.executedSql[0];
    } else if (this.sql_statements && this.sql_statements.length > 0) {
      reportQuery = this.sql_statements[tqi] ?? this.sql_statements[0];
    }
    const isCrossDomainDownload = (this.metadata?.domain ?? (this.editReport as any)?.reportMetadata?.domain) === 'cross_domain';
    const rawDownloadSourceId = this.metadata?.reportSourceId ?? (this.editReport as any)?.reportMetadata?.reportSourceId;
    const reportSourceId = isCrossDomainDownload ? undefined : rawDownloadSourceId ?? 1;
    const params: any = {
      fleetId: this.fleetId,
      reportType: 'CUSTOM_REPORT',
      reportQuery,
      reportName,
      ...(reportSourceId != null && { reportSourceId }),
    };
    this.reportService
      .downloadCustomReport(params)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.csvLoader = false;
        })
      )
      .subscribe({
        next: () => {
          this.snackBarService.success(this.translate.instant('Report downloaded successfully'));
        },
        error: (err: HttpErrorResponse) => {
          const key = err?.status === 409 ? 'tripListComponentTripDuplicate' : 'tripListComponentTripsDownloadFailure';
          this.snackBarService.failure(this.translate.instant(key));
        },
      });
  }

  private initializeEditMode(): void {
    if (!this.editReport) return;
    this.isFirstEditQuestion = true;

    // Pre-fill currentQuestion so filter changes in edit mode always have a valid question
    this.currentQuestion = this.editReport.reportPayload?.question?.trim() || (this.editReport as any).reportName?.trim() || '';

    this.selectedPeriod = this.editReport.reportPayload?.dateRange ?? 'Past 7 days';
    const matchedEditPeriod = this.reportPeriods.find((p: any) => p.label === this.selectedPeriod || p.days === this.selectedPeriod);
    if (!matchedEditPeriod && this.selectedPeriod) {
      this.reportPeriods = [{ label: this.selectedPeriod, days: this.selectedPeriod }, ...TALK_TO_DATA_DATE_RANGES];
    }
    this.calculateDateRange(this.selectedPeriod);
    this.lastUsedPeriod = this.selectedPeriod;

    const savedTagIds = this.editReport.reportPayload?.tagIds ?? [];
    if (savedTagIds.length > 0) {
      this.selectedTagIds = [...savedTagIds];
      this.filterSelectedTagIds = [...savedTagIds];
      this.lastUsedTagIds = [...savedTagIds];
      this.selectedTagNames = this.getTagNamesByIds(savedTagIds);
      this.lastUsedTagNames = [...this.selectedTagNames];
      this.userTags = this.getTagObjectsByIds(savedTagIds);
      this.assignedTagList = [...savedTagIds];
    }

    if (this.reportQuery) {
      this.sql = this.executedSql = this.sql_statements = [this.reportQuery];
    }

    // Populate metadata from saved report so reportSourceId/tableQueryIndex
    // are present in the edit payload even when no new query is run
    if (this.editReport.reportMetadata) {
      this.metadata = {
        date_range: this.editReport.reportPayload?.dateRange ?? '',
        sql_where_date_columns: (this.editReport as any).reportMetadata?.sql_where_date_columns ?? [],
        tag_ids: this.editReport.reportPayload?.tagIds ?? [],
        ...(this.editReport as any).reportMetadata,
      };
    }

    this.conversationState = 'report_generated';
    this.isFirstQuery = false;
    this.loadExistingReport();
  }

  private loadExistingReport(): void {
    if (!this.editReport) return;

    this.isLoading = true;
    this.showNoDataMessage = false;

    // Fetch saved conversation history and preview data in parallel
    this.reportService
      .getReportConversation(this.customReportId, this.clientId, this.fleetId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const savedTurns: ReportTurn[] = res?.conversation ?? [];
          this.turns = [...savedTurns];
          this.replayConversationTurns(savedTurns);
          this.loadPreviewData();
        },
        error: () => {
          // No saved conversation — just load the data
          this.loadPreviewData();
        },
      });
  }

  private replayConversationTurns(savedTurns: ReportTurn[]): void {
    savedTurns.forEach((turn) => {
      this.addUserMessage(turn.question);
      this.addBotMessage(turn.answer, { requestId: turn.requestId });
      if (turn.sql) {
        this.addSqlThinkingMessage(turn.sql);
      }
    });
    if (savedTurns.length > 0) {
      const last = savedTurns[savedTurns.length - 1];
      this.currentQuestion = last.question;
      this.sql_statements = last.sql ? [last.sql] : [];
      this.executedSql = last.executed_sql ? [last.executed_sql] : [];
      this.sql = this.sql_statements;
      if (last.metadata) {
        this.metadata = last.metadata;
        const restoredPeriod = last.metadata.date_range || this.selectedPeriod;
        this.selectedPeriod = restoredPeriod;
        const matchedReplayPeriod = this.reportPeriods.find((p: any) => p.label === restoredPeriod || p.days === restoredPeriod);
        if (!matchedReplayPeriod && restoredPeriod) {
          this.reportPeriods = [{ label: restoredPeriod, days: restoredPeriod }, ...TALK_TO_DATA_DATE_RANGES];
        }
      }
    }
  }

  private loadPreviewData(): void {
    if (!this.editReport) return;

    const domain = (this.editReport as any)?.reportMetadata?.domain ?? this.metadata?.domain;
    const isCrossDomain = domain === 'cross_domain';
    const rawReportSourceId = (this.editReport as any)?.reportMetadata?.reportSourceId ?? this.metadata?.reportSourceId;
    const previewReportSourceId = isCrossDomain ? undefined : rawReportSourceId ?? 1;
    this.reportService
      .getCustomReportData(this.customReportId, {
        reportQuery: this.reportQuery ?? '',
        ...(previewReportSourceId != null && { reportSourceId: previewReportSourceId }),
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: (response: any) => {
          this.processEditModeResponse(response as { rows: ReportDataRow[]; totalCount: number });
        },
        error: () => this.loadFromSavedData(),
      });
  }

  private processEditModeResponse(response: { rows: ReportDataRow[]; totalCount: number }): void {
    const rows = response?.rows;

    if (!rows?.length) {
      this.showNoDataMessage = true;
      this.dataSource = [];
      this.displayedColumns = [];
      this.addBotMessage(
        this.translate.instant(
          'The report loaded but returned no data for the saved filters. You can adjust the period or tags and regenerate.'
        )
      );
      return;
    }

    this.dataSource = rows;
    this.displayedColumns = Object.keys(rows[0]);
    if (this.dataSource.length > 20 && this.selectedCanvasTab === 1) {
      this.selectedCanvasTab = 0;
    }
    this.showNoDataMessage = false;
    this.showNoDataRetrySection = false;
    this.conversationState = 'report_generated';
    this.hasResult = true;
    this.canvasTitle = (this.editReport?.reportName ?? this.currentQuestion ?? 'Report').substring(0, 60);

    if (this.turns.length === 0) {
      this.addBotMessage(
        this.translate.instant(
          'Here is your saved report. You can type a new question below to modify it, or use the filters on the right to adjust the period and tags.'
        )
      );
    }

    setTimeout(() => this.openCanvas(), 400);
  }

  private loadFromSavedData(): void {
    if (!this.editReport) return;

    const savedData = this.editReport.reportPayload?.data ?? [];
    const savedColumns = this.editReport.reportPayload?.columns ?? [];

    if (savedData.length > 0) {
      this.dataSource = savedData;
      this.displayedColumns = savedColumns.length > 0 ? savedColumns : Object.keys(savedData[0]);
      this.showNoDataMessage = false;
      this.showNoDataRetrySection = false;
      this.conversationState = 'report_generated';
      this.addBotMessage(
        this.translate.instant(
          'Here is your saved report. You can type a new question below to modify it, or use the filters on the right to adjust the period and tags.'
        )
      );
    } else {
      this.showNoDataMessage = true;
      this.dataSource = [];
      this.displayedColumns = [];
      this.addBotMessage(
        this.translate.instant('Could not load the saved report data. Please type a question below to generate a new report.')
      );
    }
  }

  private getCurrentDomain(): ReportDomain {
    return (
      this.metadata?.domain ||
      this.data?.domain ||
      (this.editReport as any)?.reportMetadata?.domain ||
      this.editReport?.reportPayload?.metadata?.domain ||
      'trip'
    );
  }

  public getCurrentReportSourceId(): number | null {
    const sourceId =
      this.metadata?.reportSourceId ||
      this.data?.reportSourceId ||
      (this.editReport as any)?.reportMetadata?.reportSourceId ||
      this.editReport?.reportPayload?.metadata?.reportSourceId ||
      null;

    return Number.isInteger(sourceId) ? Number(sourceId) : null;
  }

  public getCurrentCrossDomainPlan(): any {
    return (
      this.metadata?.crossDomainPlan ??
      this.data?.crossDomainPlan ??
      (this.editReport as any)?.reportMetadata?.crossDomainPlan ??
      this.editReport?.reportPayload?.metadata?.crossDomainPlan ??
      null
    );
  }

  public get hasValidReportSourceId(): boolean {
    return this.getCurrentReportSourceId() !== null;
  }

  public get isCrossDomain(): boolean {
    return this.getCurrentDomain() === 'cross_domain';
  }

  public get isWorkflowAwaitingInput(): boolean {
    const hasSql = this.getCurrentSqlStatements().length > 0;
    const hasData = Array.isArray(this.dataSource) && this.dataSource.length > 0;
    return this.getCurrentDomain() === 'workflow' && !hasSql && !hasData;
  }

  public get isActionControlsDisabled(): boolean {
    return this.isWorkflowAwaitingInput;
  }

  public get isSaveDisabled(): boolean {
    return this.isWorkflowAwaitingInput || this.isCrossDomain;
  }

  private toSqlArray(sqlValue: unknown): string[] {
    if (Array.isArray(sqlValue)) {
      return sqlValue.filter((sql) => typeof sql === 'string' && sql.trim().length > 0);
    }
    if (typeof sqlValue === 'string' && sqlValue.trim().length > 0) {
      return [sqlValue];
    }
    return [];
  }

  private getCurrentSqlStatements(): string[] {
    const generatedSql = this.toSqlArray(this.sql_statements);
    if (generatedSql.length) {
      return generatedSql;
    }

    if (this.isEditMode) {
      const editModeSql = this.toSqlArray(this.data?.sql);
      if (editModeSql.length) {
        return editModeSql;
      }
    }

    const executedSql = this.toSqlArray(this.executedSql);
    if (executedSql.length) {
      return executedSql;
    }

    return this.toSqlArray(this.sql);
  }

  private getSqlPayloadForLlm(sqlStatements: string[]): string | string[] {
    if (this.getCurrentDomain() === 'cross_domain') return sqlStatements;
    const tqi = this.metadata?.tableQueryIndex ?? 0;
    return sqlStatements[tqi] ?? sqlStatements[0] ?? '';
  }
}
